import { formatDateToShortString } from '@/src/common/utils/date'
import { formatCentsAsDollars, formatCurrency } from '@/src/common/utils/general'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks/useCardAccount'
import { useNewestApplication } from '@/src/experiences/card-application/hooks/useNewestApplication'
import { CardApplicationApi } from '@/src/experiences/card-application/stores'
import type { BalanceComponentType } from '@/src/experiences/card-payments/screens/MakePayment/Balance'
import { PointsForStatementCreditApi } from '@/src/experiences/points-to-statement-credit/stores'
import type { ComponentProps } from 'react'
import { useEvent } from 'tamagui'

export function PlatformBalance({ BalanceComponent }: { BalanceComponent: BalanceComponentType }) {
  const cardAccount = useCardAccount()
  const cardAccountId = cardAccount.data?.[0]?.id
  const applicationId = useNewestApplication().data?.newestApplication?.id
  const refetchCardAccountSummary = useEvent(() => void cardAccount.refetch())

  return cardAccountId ? (
    <PlatformBalanceForCardAccount
      BalanceComponent={BalanceComponent}
      cardAccountId={cardAccountId}
      applicationId={applicationId}
    />
  ) : (
    <BalanceComponent refetchCardAccountSummary={refetchCardAccountSummary} />
  )
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore "Type instantiation is excessively deep and possibly infinite." but DOOM so we good
type CardAccountId = NonNullable<ReturnType<typeof useCardAccount>['data']>[number]['id']
type ApplicationId = NonNullable<
  NonNullable<ReturnType<typeof useNewestApplication>['data']>['newestApplication']
>['id']

function useCardAccountSummary(cardAccountId: CardAccountId) {
  return PointsForStatementCreditApi.endpoints['GET /card/accounts/:cardAccountId'].useQuery({
    params: { cardAccountId },
  })
}

function useTerms(applicationId: ApplicationId) {
  return CardApplicationApi.endpoints[
    'GET /card/onboarding/applications/:applicationId/terms'
  ].useQuery({ params: { applicationId } })
}

type RenderBalanceComponent = (termsQuery?: ReturnType<typeof useTerms>) => React.ReactNode

function PlatformBalanceForCardAccount({
  BalanceComponent,
  cardAccountId,
  applicationId,
}: {
  BalanceComponent: BalanceComponentType
  cardAccountId: CardAccountId
  applicationId?: ApplicationId
}) {
  const query = useCardAccountSummary(cardAccountId)
  const refetchCardAccountSummary = useEvent(() => void query.refetch())

  const renderBalanceComponent = ((termsQuery?) => {
    const termsProps = termsQuery?.data
      ? ({
          paymentCardCreditLine:
            typeof termsQuery.data.creditLimitCents === 'number'
              ? {
                  value: termsQuery.data.creditLimitCents,
                  formatted: formatCurrency(termsQuery.data.creditLimitCents),
                }
              : undefined,
          purchaseAPR: termsQuery.data.interestRatePercentage,
        } as const satisfies Pick<
          ComponentProps<typeof BalanceComponent>,
          'paymentCardCreditLine' | 'purchaseAPR'
        >)
      : undefined

    if (!query.data) {
      return (
        <BalanceComponent {...termsProps} refetchCardAccountSummary={refetchCardAccountSummary} />
      )
    }

    const { summary } = query.data

    return (
      <BalanceComponent
        {...termsProps}
        refetchCardAccountSummary={refetchCardAccountSummary}
        statementBalance={{
          value: summary.remainingStatementBalanceCents,
          formatted: formatCentsAsDollars(summary.remainingStatementBalanceCents),
        }}
        currentBalance={{
          value: summary.currentBalance.totalCents,
          formatted: formatCentsAsDollars(summary.currentBalance.totalCents),
        }}
        minimumPayment={{
          value: summary.minimumPaymentDueCents.toString(),
          formatted: formatCentsAsDollars(summary.minimumPaymentDueCents),
        }}
        statementDueOn={(() => {
          if (!summary.paymentDueDate) return undefined
          const value = new Date(summary.paymentDueDate)
          return { value, formatted: formatDateToShortString(value) }
        })()}
        paymentCardNextClosingDate={
          summary.statementCloseDate
            ? formatDateToShortString(new Date(summary.statementCloseDate))
            : undefined
        }
        availableCredit={{
          value: summary.availableCredit.totalCents,
          formatted: formatCurrency(summary.availableCredit.totalCents),
        }}
      />
    )
  }) satisfies RenderBalanceComponent

  return applicationId ? (
    <PlatformBalanceWithTerms
      applicationId={applicationId}
      renderBalanceComponent={renderBalanceComponent}
    />
  ) : (
    renderBalanceComponent()
  )
}

function PlatformBalanceWithTerms({
  applicationId,
  renderBalanceComponent,
}: {
  applicationId: ApplicationId
  renderBalanceComponent: RenderBalanceComponent
}) {
  return renderBalanceComponent(useTerms(applicationId))
}
